import type { AdvanceRequest, Debt, LoanStatus } from '../types';
import * as lendingApi from '../api/lending';
import { getFarmersByHtx } from './farmers';
import { KeyedAsyncCache } from './asyncCache';

/** Loan approval/disbursement/limit-granting are Agrida-internal actions (CO → DEPT_HEAD
 * → DIRECTOR, then OPS/ADMIN for disbursement) done via the separate Admin Portal BFF —
 * HTX_STAFF has no accessible endpoint for any of them (verified against the real
 * controllers' @Roles() gates). This module only exposes what HTX/Farmer can actually
 * do: submit, request-more-info, resubmit, and read status — see task 27 notes. */

const STATUS_FROM_BACKEND: Record<lendingApi.BackendLoanStatus, LoanStatus> = {
  PENDING: 'Chờ kiểm tra',
  NEED_MORE_INFO: 'Cần bổ sung',
  APPROVED: 'Đã duyệt',
  REJECTED: 'Từ chối',
  DISBURSED: 'Đã giải ngân',
  ACTIVE: 'Đã giải ngân',
  SETTLED: 'Đã tất toán',
  OVERDUE: 'Đã giải ngân',
  CANCELLED: 'Đã hủy',
};

function toFrontendLoan(l: lendingApi.BackendLoan): AdvanceRequest {
  return {
    id: l._id,
    maKhoanVay: l.code,
    farmerId: l.borrowerId,
    borrowerType: l.borrowerType,
    seasonId: l.seasonId,
    contractId: l.contractId,
    loai: l.loanType === 'MATERIAL' ? 'Vật tư' : 'Tiền',
    soTien: Number(l.principalVnd),
    mucDich: l.purpose || '',
    tenVatTu: l.materialName,
    soLuongVatTu: l.materialQuantity,
    donViVatTu: l.materialUnit,
    nhomVatTu: l.materialGroup,
    lyDoTuChoi: l.rejectionReason,
    noiDungCanBoSung: l.needMoreInfoNote,
    isOverLimit: l.isOverLimit,
    ngayYeuCau: l.createdAt,
    ngayGiaiNgan: l.disbursedAt,
    ngayTatToan: l.settledAt,
    laiSuat: l.interestRatePercentPerYear,
    status: STATUS_FROM_BACKEND[l.status],
  };
}

const VN_OFFSET_MS = 7 * 60 * 60 * 1000;

/** The backend's dueDate as a VN calendar day. Recomputing it here with
 * setMonth followed the browser's timezone and overflowed short months, and
 * a loan with no term came out "due" the day it was disbursed. */
function loanDueDate(loan: lendingApi.BackendLoan): string {
  if (!loan.dueDate) return '';
  return new Date(new Date(loan.dueDate).getTime() + VN_OFFSET_MS).toISOString().slice(0, 10);
}

function toDebtStatus(loan: lendingApi.BackendLoan): Debt['status'] {
  if (loan.status === 'SETTLED') return 'Đã tất toán';
  if (loan.status === 'OVERDUE') return 'Quá hạn';
  const due = loanDueDate(loan);
  if (due && due < new Date().toISOString().slice(0, 10)) return 'Quá hạn';
  return 'Đang vay';
}

// The real backend has no Debt schema — a disbursed (ACTIVE/OVERDUE) CASH loan *is*
// the outstanding debt until SETTLED. There's no partial-repayment tracking at the
// loan level either (repayment is a single PaymentIntent settling the whole loan —
// see store/payments.ts), so `conNo` is binary: the full principal, or 0 once settled.
const DEBT_ELIGIBLE_STATUSES: lendingApi.BackendLoanStatus[] = ['DISBURSED', 'ACTIVE', 'SETTLED', 'OVERDUE'];

function toDebt(loan: lendingApi.BackendLoan): Debt {
  return {
    id: loan._id,
    farmerId: loan.borrowerId,
    advanceRequestId: loan._id,
    maKhoanVay: loan._id.slice(-8).toUpperCase(),
    soTien: Number(loan.principalVnd),
    daTra: loan.status === 'SETTLED' ? Number(loan.principalVnd) : 0,
    conNo: loan.status === 'SETTLED' ? 0 : Number(loan.principalVnd),
    ngayDenHan: loanDueDate(loan),
    status: toDebtStatus(loan),
    laiSuat: loan.interestRatePercentPerYear,
    ngayVay: loan.disbursedAt,
  };
}

// One cache of the raw backend records per farmer — both the `AdvanceRequest[]` view
// (all loans) and the `Debt[]` view (disbursed CASH loans only) derive from it, so
// there's a single fetch per farmer rather than two independent ones.
const rawByFarmer = new KeyedAsyncCache<lendingApi.BackendLoan[]>(
  (farmerId) => lendingApi.listLoansByFarmer(farmerId),
  [],
  'loans:rawByFarmer',
);

// HTX-direct loans (borrowerType HTX) live under a separate cache — they'd
// never surface from a per-farmer fetch since their borrowerId isn't any
// farmer's id at all.
const rawByHtxBorrower = new KeyedAsyncCache<lendingApi.BackendLoan[]>(
  (htxId) => lendingApi.listLoansByHtxBorrower(htxId),
  [],
  'loans:rawByHtxBorrower',
);

export function getLoansByFarmer(farmerId?: string): AdvanceRequest[] {
  if (!farmerId) return [];
  return rawByFarmer.get(farmerId).map(toFrontendLoan);
}

export function getDebtsByFarmer(farmerId?: string): Debt[] {
  if (!farmerId) return [];
  return rawByFarmer
    .get(farmerId)
    .filter((l) => l.loanType === 'CASH' && DEBT_ELIGIBLE_STATUSES.includes(l.status))
    .map(toDebt);
}

/** No backend endpoint lists "all loans for my HTX" for the HTX_STAFF role (`GET
 * /loans` is Agrida-internal-only) — composed client-side from the farmer roster
 * instead, plus the HTX's own direct loans (borrowerType HTX) fetched separately
 * since they don't belong to any farmer. Fine at cooperative scale; would need a
 * real aggregate endpoint at hundreds-of-farmers scale. */
export function getLoansByHtx(htxId?: string): AdvanceRequest[] {
  if (!htxId) return [];
  return [...getFarmersByHtx(htxId).flatMap((f) => getLoansByFarmer(f.id)), ...getHtxDirectLoans(htxId)];
}

/** The HTX's own direct loans only — not a farmer's. */
export function getHtxDirectLoans(htxId?: string): AdvanceRequest[] {
  if (!htxId) return [];
  return rawByHtxBorrower.get(htxId).map(toFrontendLoan);
}

export function getDebtsByHtx(htxId?: string): Debt[] {
  if (!htxId) return [];
  return getFarmersByHtx(htxId).flatMap((f) => getDebtsByFarmer(f.id));
}

function refresh(farmerId: string) {
  rawByFarmer.invalidate(farmerId);
  rawByFarmer.get(farmerId);
}

export async function submitLoan(data: {
  farmerId: string;
  /** Optional: the server derives it from the farmer profile. */
  htxId?: string;
  loai: 'Tiền' | 'Vật tư';
  financingProductId?: string;
  seasonId?: string;
  soTien: number;
  mucDich?: string;
  tenVatTu?: string;
  soLuongVatTu?: number;
  donViVatTu?: string;
  nhomVatTu?: string;
  selfFundedRatio?: number;
}): Promise<AdvanceRequest> {
  const created = await lendingApi.submitLoanApplication({
    farmerId: data.farmerId,
    htxId: data.htxId,
    loanType: data.loai === 'Vật tư' ? 'MATERIAL' : 'CASH',
    financingProductId: data.financingProductId,
    seasonId: data.seasonId,
    principalVnd: data.soTien,
    purpose: data.mucDich,
    materialName: data.tenVatTu,
    materialQuantity: data.soLuongVatTu,
    materialUnit: data.donViVatTu,
    materialGroup: data.nhomVatTu,
    selfFundedRatio: data.selfFundedRatio,
  });
  refresh(data.farmerId);
  return toFrontendLoan(created);
}

export async function submitHtxLoan(data: {
  htxId: string;
  loai: 'Tiền' | 'Vật tư';
  financingProductId?: string;
  soTien: number;
  mucDich?: string;
  tenVatTu?: string;
  soLuongVatTu?: number;
  donViVatTu?: string;
  nhomVatTu?: string;
}): Promise<AdvanceRequest> {
  const created = await lendingApi.submitHtxLoanApplication({
    htxId: data.htxId,
    loanType: data.loai === 'Vật tư' ? 'MATERIAL' : 'CASH',
    financingProductId: data.financingProductId,
    principalVnd: data.soTien,
    purpose: data.mucDich,
    materialName: data.tenVatTu,
    materialQuantity: data.soLuongVatTu,
    materialUnit: data.donViVatTu,
    materialGroup: data.nhomVatTu,
  });
  rawByHtxBorrower.invalidate(data.htxId);
  rawByHtxBorrower.get(data.htxId);
  return toFrontendLoan(created);
}

export async function requestLoanMoreInfo(loanId: string, note: string, farmerId: string): Promise<AdvanceRequest> {
  const updated = await lendingApi.requestMoreInfo(loanId, note);
  refresh(farmerId);
  return toFrontendLoan(updated);
}

export async function resubmitLoan(loanId: string, data: { soTien?: number; mucDich?: string }, farmerId: string): Promise<AdvanceRequest> {
  const updated = await lendingApi.resubmitLoan(loanId, { principalVnd: data.soTien, purpose: data.mucDich });
  refresh(farmerId);
  return toFrontendLoan(updated);
}

export async function cancelLoan(loanId: string, farmerId: string): Promise<AdvanceRequest> {
  const updated = await lendingApi.cancelLoan(loanId);
  refresh(farmerId);
  return toFrontendLoan(updated);
}

/** Called after a repayment PaymentIntent settles (see store/payments.ts) so the
 * loan/debt views reflect SETTLED without waiting for the cache's next natural miss. */
export function refreshLoansForFarmer(farmerId: string): void {
  refresh(farmerId);
}
